import {
  SPARK_CORE_TINT,
  sparkGradientStops,
  sparkTuning,
  spikeAlpha,
  type GlowProfile,
} from "./spark.js";

/** Resolution of the pre-rendered spike sprite. */
const SPIKE_SPRITE_SIZE = 128;

let spikeSprite: { key: string; canvas: HTMLCanvasElement } | null = null;

/** The star spikes, rendered once and rebuilt only when their shape is tuned. */
function getSpikeSprite(): HTMLCanvasElement | null {
  const { spikeWidth, spikeDiagonal } = sparkTuning;
  const key = `${spikeWidth}:${spikeDiagonal}`;
  if (spikeSprite?.key === key) return spikeSprite.canvas;

  const canvas = document.createElement("canvas");
  canvas.width = SPIKE_SPRITE_SIZE;
  canvas.height = SPIKE_SPRITE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const half = SPIKE_SPRITE_SIZE / 2;
  // At least about a pixel wide, so thin arms don't break up.
  const width = Math.max(spikeWidth, 1.5 / half);
  // Lifted halfway to white, like the show's spikes.
  const r = Math.round((SPARK_CORE_TINT.r + 255) / 2);
  const g = Math.round((SPARK_CORE_TINT.g + 255) / 2);
  const b = Math.round((SPARK_CORE_TINT.b + 255) / 2);
  const image = ctx.createImageData(SPIKE_SPRITE_SIZE, SPIKE_SPRITE_SIZE);
  for (let j = 0; j < SPIKE_SPRITE_SIZE; j++) {
    for (let i = 0; i < SPIKE_SPRITE_SIZE; i++) {
      const o = (j * SPIKE_SPRITE_SIZE + i) * 4;
      image.data[o] = r;
      image.data[o + 1] = g;
      image.data[o + 2] = b;
      image.data[o + 3] = Math.round(
        255 * spikeAlpha((i + 0.5 - half) / half, (j + 0.5 - half) / half, width, spikeDiagonal),
      );
    }
  }
  ctx.putImageData(image, 0, 0);
  spikeSprite = { key, canvas };
  return canvas;
}

/** The glowing spark sprite with star spikes, optionally stretched vertically for the flight. */
export function drawSparkSprite(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  profile: GlowProfile,
  alpha: number,
  stretch = 1,
): void {
  if (alpha <= 0) return;

  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
  for (const stop of sparkGradientStops(profile)) gradient.addColorStop(stop.offset, stop.color);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.scale(1 / Math.sqrt(stretch), stretch);
  ctx.fillStyle = gradient;
  ctx.fillRect(-radius, -radius, radius * 2, radius * 2);

  const spikes = getSpikeSprite();
  const spikeOpacity = alpha * profile.spikes * profile.twinkle * sparkTuning.spikeStrength;
  if (spikes && spikeOpacity > 0) {
    const reach = radius * sparkTuning.spikeLength;
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = Math.min(1, spikeOpacity);
    ctx.rotate(profile.spin);
    ctx.drawImage(spikes, -reach, -reach, reach * 2, reach * 2);
  }
  ctx.restore();
}
